// AI Service for OilBuddy AI Platform
// Integrates Claude Sonnet 4 and GPT-5 with oilfield-specific functionality

export interface AIModel {
  id: string
  name: string
  provider: 'anthropic' | 'openai' | 'local'
  model: string
  maxTokens: number
  costPerToken: number
  capabilities: string[]
  isAvailable: boolean
}

export interface AIAgent {
  id: string
  name: string
  type: string
  description: string
  model: string
  status: 'active' | 'inactive' | 'training'
  performance: {
    accuracy: number
    responseTime: number
    tasksCompleted: number
    costSavings: number
  }
  createdAt: string
  lastUsed: string
}

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: string
  model?: string
  tokens?: number
  cost?: number
}

export interface AIConversation {
  id: string
  agentId: string
  messages: ChatMessage[]
  totalTokens: number
  totalCost: number
  createdAt: string
  updatedAt: string
}

// Available AI Models
export const AI_MODELS: AIModel[] = [
  {
    id: 'claude-sonnet-4',
    name: 'Claude Sonnet 4',
    provider: 'anthropic',
    model: 'claude-3-5-sonnet-20241022',
    maxTokens: 200000,
    costPerToken: 0.000003,
    capabilities: ['reasoning', 'analysis', 'coding', 'creative', 'oilfield-expertise'],
    isAvailable: true
  },
  {
    id: 'gpt-5',
    name: 'GPT-5',
    provider: 'openai',
    model: 'gpt-5',
    maxTokens: 128000,
    costPerToken: 0.000005,
    capabilities: ['reasoning', 'analysis', 'coding', 'creative', 'multimodal'],
    isAvailable: true
  },
  {
    id: 'claude-haiku',
    name: 'Claude Haiku',
    provider: 'anthropic',
    model: 'claude-3-haiku-20240307',
    maxTokens: 200000,
    costPerToken: 0.00000025,
    capabilities: ['fast-response', 'analysis', 'oilfield-expertise'],
    isAvailable: true
  },
  {
    id: 'gpt-4-turbo',
    name: 'GPT-4 Turbo',
    provider: 'openai',
    model: 'gpt-4-turbo-preview',
    maxTokens: 128000,
    costPerToken: 0.00001,
    capabilities: ['reasoning', 'analysis', 'coding', 'creative'],
    isAvailable: true
  }
]

// Oilfield-specific system prompts
export const OILFIELD_PROMPTS = {
  safety: `You are an expert oilfield safety advisor with deep knowledge of OSHA regulations, industry best practices, and real-world safety scenarios. Provide practical, actionable advice for oilfield operations.`,
  compliance: `You are a compliance expert specializing in oilfield regulations, environmental laws, and industry standards. Help ensure operations meet all legal and regulatory requirements.`,
  operations: `You are an oilfield operations expert with experience in drilling, production, and field management. Provide technical guidance and operational best practices.`,
  equipment: `You are an oilfield equipment specialist with expertise in drilling rigs, production equipment, and maintenance procedures. Provide technical support and troubleshooting guidance.`,
  financial: `You are an oilfield financial advisor specializing in cost optimization, tax strategies, and financial planning for oilfield operations.`,
  general: `You are an AI assistant specialized in oilfield operations, providing expert guidance on safety, compliance, operations, equipment, and financial matters.`
}

class AIService {
  private apiKey: string | null = null
  private baseUrl: string = '/api/ai'
  private conversations: Map<string, AIConversation> = new Map()

  constructor() {
    this.loadAPIKey()
  }

  private loadAPIKey() {
    // Load API key from environment or localStorage
    this.apiKey = (import.meta as any).env?.VITE_AI_API_KEY || localStorage.getItem('ai_api_key')
  }

  // Initialize AI service
  async initialize(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })
      return response.ok
    } catch (error) {
      console.error('AI service initialization failed:', error)
      return false
    }
  }

  // Get available models
  async getAvailableModels(): Promise<AIModel[]> {
    try {
      const response = await fetch(`${this.baseUrl}/models`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        }
      })

      if (response.ok) {
        const models = await response.json()
        return models.map((model: any) => ({
          ...model,
          isAvailable: true
        }))
      } else {
        // Return default models if API is unavailable
        return AI_MODELS
      }
    } catch (error) {
      console.error('Error fetching AI models:', error)
      return AI_MODELS
    }
  }

  // Create AI agent
  async createAgent(agentData: Partial<AIAgent>): Promise<AIAgent> {
    try {
      const response = await fetch(`${this.baseUrl}/agents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify(agentData)
      })

      if (response.ok) {
        return await response.json()
      } else {
        // Create mock agent for demo
        return this.createMockAgent(agentData)
      }
    } catch (error) {
      console.error('Error creating AI agent:', error)
      return this.createMockAgent(agentData)
    }
  }

  private createMockAgent(agentData: Partial<AIAgent>): AIAgent {
    return {
      id: `agent-${Date.now()}`,
      name: agentData.name || 'New Agent',
      type: agentData.type || 'general',
      description: agentData.description || 'AI agent for oilfield operations',
      model: agentData.model || 'claude-sonnet-4',
      status: 'active',
      performance: {
        accuracy: 95,
        responseTime: 1.2,
        tasksCompleted: 0,
        costSavings: 0
      },
      createdAt: new Date().toISOString(),
      lastUsed: new Date().toISOString()
    }
  }

  // Send message to AI
  async sendMessage(
    conversationId: string,
    message: string,
    model: string = 'claude-sonnet-4',
    context?: string
  ): Promise<ChatMessage> {
    try {
      const response = await fetch(`${this.baseUrl}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          conversationId,
          message,
          model,
          context,
          timestamp: new Date().toISOString()
        })
      })

      if (response.ok) {
        const result = await response.json()
        return result.message
      } else {
        // Generate mock response for demo
        return this.generateMockResponse(message, model, context)
      }
    } catch (error) {
      console.error('Error sending message to AI:', error)
      return this.generateMockResponse(message, model, context)
    }
  }

  private generateMockResponse(message: string, model: string, context?: string): ChatMessage {
    const responses = {
      'claude-sonnet-4': [
        "Based on my analysis of oilfield operations, I recommend implementing enhanced safety protocols. The current situation requires immediate attention to ensure compliance with industry standards.",
        "From an oilfield operations perspective, this appears to be a standard procedure that should be followed according to OSHA guidelines. Let me provide you with specific recommendations.",
        "I've analyzed the technical specifications and operational requirements. Here's my assessment based on industry best practices and regulatory compliance standards."
      ],
      'gpt-5': [
        "As an AI expert in oilfield operations, I can provide comprehensive guidance on this matter. The solution involves multiple considerations including safety, efficiency, and regulatory compliance.",
        "Based on my advanced reasoning capabilities, I can see several optimization opportunities here. Let me break down the technical and operational aspects for you.",
        "I've processed the information using my latest capabilities and can provide detailed insights on the oilfield operations you've described."
      ]
    }

    const modelResponses = responses[model as keyof typeof responses] || responses['claude-sonnet-4']
    const randomResponse = modelResponses[Math.floor(Math.random() * modelResponses.length)]

    return {
      id: `msg-${Date.now()}`,
      role: 'assistant',
      content: randomResponse,
      timestamp: new Date().toISOString(),
      model,
      tokens: Math.floor(Math.random() * 500) + 100,
      cost: Math.random() * 0.01
    }
  }

  // Get conversation history
  async getConversation(conversationId: string): Promise<AIConversation | null> {
    try {
      const response = await fetch(`${this.baseUrl}/conversations/${conversationId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        }
      })

      if (response.ok) {
        return await response.json()
      } else {
        return this.conversations.get(conversationId) || null
      }
    } catch (error) {
      console.error('Error fetching conversation:', error)
      return this.conversations.get(conversationId) || null
    }
  }

  // Get all conversations
  async getConversations(): Promise<AIConversation[]> {
    try {
      const response = await fetch(`${this.baseUrl}/conversations`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        }
      })

      if (response.ok) {
        return await response.json()
      } else {
        return Array.from(this.conversations.values())
      }
    } catch (error) {
      console.error('Error fetching conversations:', error)
      return Array.from(this.conversations.values())
    }
  }

  // Get AI agents
  async getAgents(): Promise<AIAgent[]> {
    try {
      const response = await fetch(`${this.baseUrl}/agents`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        }
      })

      if (response.ok) {
        return await response.json()
      } else {
        // Return mock agents for demo
        return this.getMockAgents()
      }
    } catch (error) {
      console.error('Error fetching AI agents:', error)
      return this.getMockAgents()
    }
  }

  private getMockAgents(): AIAgent[] {
    return [
      {
        id: 'safety-advisor',
        name: 'Safety Advisor',
        type: 'safety',
        description: 'Expert safety guidance for oilfield operations',
        model: 'claude-sonnet-4',
        status: 'active',
        performance: {
          accuracy: 98,
          responseTime: 0.8,
          tasksCompleted: 1247,
          costSavings: 45000
        },
        createdAt: '2024-01-01T00:00:00Z',
        lastUsed: new Date().toISOString()
      },
      {
        id: 'compliance-checker',
        name: 'Compliance Checker',
        type: 'compliance',
        description: 'Regulatory compliance and legal guidance',
        model: 'gpt-5',
        status: 'active',
        performance: {
          accuracy: 96,
          responseTime: 1.1,
          tasksCompleted: 892,
          costSavings: 32000
        },
        createdAt: '2024-01-15T00:00:00Z',
        lastUsed: new Date().toISOString()
      },
      {
        id: 'operations-optimizer',
        name: 'Operations Optimizer',
        type: 'operations',
        description: 'Operational efficiency and process optimization',
        model: 'claude-sonnet-4',
        status: 'active',
        performance: {
          accuracy: 94,
          responseTime: 1.3,
          tasksCompleted: 567,
          costSavings: 28000
        },
        createdAt: '2024-02-01T00:00:00Z',
        lastUsed: new Date().toISOString()
      }
    ]
  }

  // Get AI statistics
  async getAIStats(): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        }
      })

      if (response.ok) {
        return await response.json()
      } else {
        // Return mock stats for demo
        return this.getMockStats()
      }
    } catch (error) {
      console.error('Error fetching AI stats:', error)
      return this.getMockStats()
    }
  }

  private getMockStats() {
    return {
      totalAgents: 8,
      activeAgents: 6,
      totalConversations: 1247,
      totalMessages: 8923,
      averageResponseTime: 1.2,
      totalCost: 234.56,
      accuracy: 96.5,
      costSavings: 125000
    }
  }

  // Deploy agent
  async deployAgent(agentId: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/agents/${agentId}/deploy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        }
      })

      return response.ok
    } catch (error) {
      console.error('Error deploying agent:', error)
      return false
    }
  }

  // Train agent
  async trainAgent(agentId: string, trainingData: any[]): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/agents/${agentId}/train`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({ trainingData })
      })

      return response.ok
    } catch (error) {
      console.error('Error training agent:', error)
      return false
    }
  }

  // Oilfield-specific AI functions
  async analyzeSafetyIncident(incidentData: any): Promise<any> {
    const prompt = `Analyze this oilfield safety incident and provide recommendations:
    ${JSON.stringify(incidentData, null, 2)}
    
    Please provide:
    1. Root cause analysis
    2. Immediate actions required
    3. Preventive measures
    4. Compliance implications
    5. Cost impact assessment`

    return this.sendMessage('safety-analysis', prompt, 'claude-sonnet-4', OILFIELD_PROMPTS.safety)
  }

  async optimizeOperations(operationData: any): Promise<any> {
    const prompt = `Analyze this oilfield operation and provide optimization recommendations:
    ${JSON.stringify(operationData, null, 2)}
    
    Please provide:
    1. Efficiency improvements
    2. Cost reduction opportunities
    3. Safety enhancements
    4. Technology recommendations
    5. Implementation timeline`

    return this.sendMessage('operations-optimization', prompt, 'gpt-5', OILFIELD_PROMPTS.operations)
  }

  async checkCompliance(complianceData: any): Promise<any> {
    const prompt = `Review this oilfield operation for compliance:
    ${JSON.stringify(complianceData, null, 2)}
    
    Please provide:
    1. Regulatory requirements
    2. Compliance status
    3. Required actions
    4. Documentation needs
    5. Risk assessment`

    return this.sendMessage('compliance-check', prompt, 'claude-sonnet-4', OILFIELD_PROMPTS.compliance)
  }

  async generateReport(reportData: any, type: string): Promise<any> {
    const prompt = `Generate a comprehensive ${type} report for this oilfield data:
    ${JSON.stringify(reportData, null, 2)}
    
    Please include:
    1. Executive summary
    2. Detailed analysis
    3. Key findings
    4. Recommendations
    5. Action items
    6. Appendices with supporting data`

    return this.sendMessage('report-generation', prompt, 'gpt-5', OILFIELD_PROMPTS.general)
  }
}

// Export singleton instance
export const aiService = new AIService()
export default aiService
